import { trackSiteSearch } from '@snowplow/browser-plugin-site-tracking';
export function extractSchemaKey(schema) {
    if (!schema.description) {
        throw new Error('Schema must have a description.');
    }
    const parsedDescription = JSON.parse(schema.description);
    if (!parsedDescription._schema_key) {
        throw new Error('Schema description must contain a _schema_key.');
    }
    return parsedDescription._schema_key;
}
export function getSchemaVersion(schema) {
    const schemaKey = extractSchemaKey(schema);
    const versionRegex = /([^/]+)$/;
    const match = schemaKey.match(versionRegex);
    // If there's no match or the last segment doesn't have any characters after the slash, return empty string
    if (!match || match[1] === '') {
        throw new Error(`Invalid schema key format: ${schemaKey}`);
    }
    return match[1];
}
export function getBigQueryColumnName(schema) {
    if (!schema.description) {
        throw new Error('Schema must have a description.');
    }
    const schemaKey = extractSchemaKey(schema);
    const parsedDescription = JSON.parse(schema.description);
    const type = parsedDescription.meta.schemaType;
    const regex = /:([^/]+)\/([^/]+)(?:\/[^/]+)*\/([^-/]+)(?:-.*)?$/;
    const match = schemaKey.match(regex);
    if (!match) {
        throw new Error(`Invalid schema key format: ${schemaKey}`);
    }
    const vendor = match[1].replace(/\./g, '_');
    const event = match[2];
    const majorVersion = match[3];
    const prefix = type === 'event' ? 'unstruct_event' : 'contexts';
    return `${prefix}_${vendor}_${event}_${majorVersion}`;
}
let trackSelfDescribingEvent;
/**
 * Tracks a self-describing event with strongly typed validation using Zod schemas and logs any runtime zod errors.
 *
 * This function provides a type-safe wrapper around the Snowplow `trackSelfDescribingEvent` function.
 * It ensures that the event and context data conform to their respective schemas before tracking.
 *
 * @param event - The event object containing a Zod schema and data conforming to that schema.
 * @param context - An array of context objects, each with its own Zod schema and matching data.
 * @param safeParse - Optional boolean indicating whether to use Zod's `safeParse` (error-tolerant validation). Defaults to true.
 * @param event - The event object containing a Zod schema and data conforming to that schema.
 * @param context - An array of context objects, each with its own Zod schema and matching data.
 * @param safeParse - Optional boolean indicating whether to use Zod's `safeParse` (error-tolerant validation). Defaults to true.
 *
 * @example
 * trackSelfDescribingEventTypeSafe({
 *   event: {
 *     schema: exampleBasicEventEventSchemaV1,
 *     data: { id: 123, name: 'example event' },
 *   },
 *   context: [
 *     { schema: exampleBasicEntityEntitySchemaV1, data: { id: 123, name: 'example' } },
 *     { schema: exampleAnotherBasicEntityEntitySchemaV1, data: { id: 123, name: 'another-example' } }
 *   ],
 * });
 */
export const trackSelfDescribingEventTypeSafe = async ({ event, context, safeParse = true, trackers, }) => {
    validateData(event.schema, event.data, 'Event', undefined, safeParse);
    context.forEach((ctx, index) => {
        validateData(ctx.schema, ctx.data, 'Context', index, safeParse);
    });
    const eventSchemaKey = extractSchemaKey(event.schema);
    const contextSchemas = context.map((ctx) => ({
        schema: extractSchemaKey(ctx.schema),
        data: ctx.data,
    }));
    if (!trackSelfDescribingEvent) {
        // Lazy load the self-describing event tracking function to avoid unnecessary imports
        trackSelfDescribingEvent = (await import('@snowplow/browser-tracker')).trackSelfDescribingEvent;
    }
    trackSelfDescribingEvent({
        event: {
            schema: eventSchemaKey,
            data: event.data,
        },
        context: contextSchemas,
    }, trackers);
};
let trackPageView;
export function validateData(schema, data, type, index, safeParse = true) {
    const validationResult = safeParse ? schema.safeParse(data) : schema.parse(data);
    const schemaKey = extractSchemaKey(schema);
    if (!validationResult.success) {
        const contextInfo = index !== undefined ? ` for context at index ${index}` : '';
        console.warn(`${type} ${schemaKey} validation failed${contextInfo}:`, validationResult.error);
        return false;
    }
    return true;
}
export const trackPageViewTypeSafe = async ({ trackers, context, safeParse = true, }) => {
    context?.forEach((ctx, index) => {
        validateData(ctx.schema, ctx.data, 'Context', index, safeParse);
    });
    if (!trackPageView) {
        // Lazy load the button click tracking plugin to avoid unnecessary imports
        trackPageView = (await import('@snowplow/browser-tracker')).trackPageView;
    }
    trackPageView({
        context: context?.map((ctx) => ({
            schema: extractSchemaKey(ctx.schema),
            data: ctx.data,
        })),
    }, trackers);
};
let enableButtonClickTracking;
export const enableButtonClickTrackingTypeSafe = async (config) => {
    config?.context?.forEach((ctx, index) => {
        validateData(ctx.schema, ctx.data, 'Context', index);
    });
    if (!enableButtonClickTracking) {
        // Lazy load the button click tracking plugin to avoid unnecessary imports
        enableButtonClickTracking = (await import('@snowplow/browser-plugin-button-click-tracking'))
            .enableButtonClickTracking;
    }
    enableButtonClickTracking({
        context: config?.context?.map((ctx) => ({
            schema: extractSchemaKey(ctx.schema),
            data: ctx.data,
        })),
    });
};
let enableLinkClickTracking;
export const enableLinkClickTrackingTypeSafe = async (config) => {
    config?.context?.forEach((ctx, index) => {
        validateData(ctx.schema, ctx.data, 'Context', index);
    });
    if (!enableLinkClickTracking) {
        // Lazy load the link click tracking plugin to avoid unnecessary imports
        enableLinkClickTracking = (await import('@snowplow/browser-plugin-link-click-tracking')).enableLinkClickTracking;
    }
    enableLinkClickTracking({
        context: config?.context?.map((ctx) => ({
            schema: extractSchemaKey(ctx.schema),
            data: ctx.data,
        })),
        trackContent: config?.trackContent,
    });
};
/**
 * Helper function to create wrapper for dynamic context functions
 */
function createDynamicContextWrapper(contextFunction, index) {
    return (...args) => {
        const contextParams = parseFormEventParams(args);
        if (!contextParams) {
            return null;
        }
        const contextResult = contextFunction(contextParams);
        if (contextResult === null || !contextResult) {
            return null;
        }
        // Validate the function result at event time
        const isValid = validateData(contextResult.schema, contextResult.data, 'Dynamic Context', index);
        if (!isValid) {
            return null;
        }
        return {
            schema: extractSchemaKey(contextResult.schema),
            data: contextResult.data,
        };
    };
}
/**
 * Parse Snowplow form event parameters into typed objects
 */
function parseFormEventParams(args) {
    if (args.length === 3) {
        // Form change/focus events: (element, eventType, value)
        return {
            element: args[0],
            eventType: args[1],
            value: args[2],
        };
    }
    else if (args.length === 2) {
        // Form submission events: (element, innerElements)
        return {
            element: args[0],
            innerElements: args[1],
        };
    }
    console.warn('Unexpected form context function parameters:', args);
    return null;
}
/**
 * Type guard to check if context item is a function
 */
function isDynamicContext(ctx) {
    return typeof ctx === 'function';
}
let enableFormTracking;
/**
 * Enable form tracking with type-safe context validation.
 * Supports both static context objects and dynamic context functions.
 *
 * @param config - Configuration object with optional context array
 * @param config.context - Array of context items (static objects or dynamic functions)
 *
 * @example
 * // Static context
 * enableFormTrackingTypeSafe({
 *   context: [{ schema: mySchema, data: { userId: 123 } }]
 * });
 *
 * @example
 * // Dynamic context function
 * enableFormTrackingTypeSafe({
 *   context: [
 *     ({ element, eventType, value }) => ({
 *       schema: myDynamicSchema,
 *       data: { elementId: element.id, eventType, value }
 *     })
 *   ]
 * });
 */
export const enableFormTrackingTypeSafe = async (config) => {
    if (!enableFormTracking) {
        // Lazy load the form tracking plugin to avoid unnecessary imports
        enableFormTracking = (await import('@snowplow/browser-plugin-form-tracking')).enableFormTracking;
    }
    if (!config?.context) {
        // Extract valid FormTrackingConfiguration properties when no context
        const { context: _, ...formTrackingConfig } = config ?? {};
        enableFormTracking(formTrackingConfig);
        return;
    }
    // Validate static context immediately
    config.context.forEach((ctx, index) => {
        if (!isDynamicContext(ctx)) {
            validateData(ctx.schema, ctx.data, 'Context', index);
        }
    });
    const processedContext = config.context.map((ctx, index) => {
        if (isDynamicContext(ctx)) {
            return createDynamicContextWrapper(ctx, index);
        }
        // Static context object
        return {
            schema: extractSchemaKey(ctx.schema),
            data: ctx.data,
        };
    });
    // Extract valid FormTrackingConfiguration properties
    const { context: _, ...formTrackingConfig } = config;
    enableFormTracking({
        ...formTrackingConfig,
        context: processedContext,
    });
};
/**
 * Tracks a site search event with the provided terms and optional filters and metrics.
 *
 * @param params Configuration for the site search tracking
 * @param params.terms Search terms entered by the user as an array
 * @param params.filters Optional filters applied to the search (e.g., categories)
 * @param params.totalResults Optional number of search results found
 * @param params.pageResults Optional number of search results displayed on the page
 * @param params.context Optional context entities to attach to the event
 * @param params.trackers Optional array of tracker identifiers
 */
export const trackSiteSearchTypeSafe = ({ terms, filters, totalResults, pageResults, context, trackers, }) => {
    context?.forEach((ctx, index) => {
        validateData(ctx.schema, ctx.data, 'Context', index);
    });
    trackSiteSearch({
        terms,
        filters,
        totalResults,
        pageResults,
        context: context?.map((ctx) => ({
            schema: extractSchemaKey(ctx.schema),
            data: ctx.data,
        })),
    }, trackers);
};
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoidHlwZXNhZmUtc2VsZi1kZXNjcmliaW5nLWV2ZW50LXRyYWNrZXIuanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi9zcmMvbGliL3R5cGVzYWZlLXNlbGYtZGVzY3JpYmluZy1ldmVudC10cmFja2VyLnRzeCJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFFQSxPQUFPLEVBQUUsZUFBZSxFQUFFLE1BQU0sd0NBQXdDLENBQUM7QUFVekUsTUFBTSxVQUFVLGdCQUFnQixDQUFDLE1BQW9CO0lBQ25ELElBQUksQ0FBQyxNQUFNLENBQUMsV0FBVyxFQUFFLENBQUM7UUFDeEIsTUFBTSxJQUFJLEtBQUssQ0FBQyxpQ0FBaUMsQ0FBQyxDQUFDO0lBQ3JELENBQUM7SUFDRCxNQUFNLGlCQUFpQixHQUFHLElBQUksQ0FBQyxLQUFLLENBQUMsTUFBTSxDQUFDLFdBQVcsQ0FBMkIsQ0FBQztJQUNuRixJQUFJLENBQUMsaUJBQWlCLENBQUMsV0FBVyxFQUFFLENBQUM7UUFDbkMsTUFBTSxJQUFJLEtBQUssQ0FBQyxnREFBZ0QsQ0FBQyxDQUFDO0lBQ3BFLENBQUM7SUFDRCxPQUFPLGlCQUFpQixDQUFDLFdBQVcsQ0FBQztBQUN2QyxDQUFDO0FBRUQsTUFBTSxVQUFVLGdCQUFnQixDQUFDLE1BQW9CO0lBQ25ELE1BQU0sU0FBUyxHQUFHLGdCQUFnQixDQUFDLE1BQU0sQ0FBQyxDQUFDO0lBQzNDLE1BQU0sWUFBWSxHQUFHLFVBQVUsQ0FBQztJQUNoQyxNQUFNLEtBQUssR0FBRyxTQUFTLENBQUMsS0FBSyxDQUFDLFlBQVksQ0FBQyxDQUFDO0lBRTVDLDJHQUEyRztJQUMzRyxJQUFJLENBQUMsS0FBSyxJQUFJLEtBQUssQ0FBQyxDQUFDLENBQUMsS0FBSyxFQUFFLEVBQUUsQ0FBQztRQUM5QixNQUFNLElBQUksS0FBSyxDQUFDLDhCQUE4QixTQUFTLEVBQUUsQ0FBQyxDQUFDO0lBQzdELENBQUM7SUFFRCxPQUFPLEtBQUssQ0FBQyxDQUFDLENBQUMsQ0FBQztBQUNsQixDQUFDO0FBRUQsTUFBTSxVQUFVLHFCQUFxQixDQUFDLE1BQW9CO0lBQ3hELElBQUksQ0FBQyxNQUFNLENBQUMsV0FBVyxFQUFFLENBQUM7UUFDeEIsTUFBTSxJQUFJLEtBQUssQ0FBQyxpQ0FBaUMsQ0FBQyxDQUFDO0lBQ3JELENBQUM7SUFDRCxNQUFNLFNBQVMsR0FBRyxnQkFBZ0IsQ0FBQyxNQUFNLENBQUMsQ0FBQztJQUMzQyxNQUFNLGlCQUFpQixHQUFHLElBQUksQ0FBQyxLQUFLLENBQUMsTUFBTSxDQUFDLFdBQVcsQ0FBdUIsQ0FBQztJQUMvRSxNQUFNLElBQUksR0FBRyxpQkFBaUIsQ0FBQyxJQUFJLENBQUMsVUFBVSxDQUFDO0lBQy9DLE1BQU0sS0FBSyxHQUFHLGtEQUFrRCxDQUFDO0lBQ2pFLE1BQU0sS0FBSyxHQUFHLFNBQVMsQ0FBQyxLQUFLLENBQUMsS0FBSyxDQUFDLENBQUM7SUFFckMsSUFBSSxDQUFDLEtBQUssRUFBRSxDQUFDO1FBQ1gsTUFBTSxJQUFJLEtBQUssQ0FBQyw4QkFBOEIsU0FBUyxFQUFFLENBQUMsQ0FBQztJQUM3RCxDQUFDO0lBRUQsTUFBTSxNQUFNLEdBQUcsS0FBSyxDQUFDLENBQUMsQ0FBQyxDQUFDLE9BQU8sQ0FBQyxLQUFLLEVBQUUsR0FBRyxDQUFDLENBQUM7SUFDNUMsTUFBTSxLQUFLLEdBQUcsS0FBSyxDQUFDLENBQUMsQ0FBQyxDQUFDO0lBQ3ZCLE1BQU0sWUFBWSxHQUFHLEtBQUssQ0FBQyxDQUFDLENBQUMsQ0FBQztJQUU5QixNQUFNLE1BQU0sR0FBRyxJQUFJLEtBQUssT0FBTyxDQUFDLENBQUMsQ0FBQyxnQkFBZ0IsQ0FBQyxDQUFDLENBQUMsVUFBVSxDQUFDO0lBRWhFLE9BQU8sR0FBRyxNQUFNLElBQUksTUFBTSxJQUFJLEtBQUssSUFBSSxZQUFZLEVBQUUsQ0FBQztBQUN4RCxDQUFDO0FBRUQsSUFBSSx3QkFHSyxDQUFDO0FBRVY7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7OztHQXdCRztBQUNILE1BQU0sQ0FBQyxNQUFNLGdDQUFnQyxHQUFHLEtBQUssRUFHbkQsRUFDQSxLQUFLLEVBQ0wsT0FBTyxFQUNQLFNBQVMsR0FBRyxJQUFJLEVBQ2hCLFFBQVEsR0FNVCxFQUFpQixFQUFFO0lBQ2xCLFlBQVksQ0FBQyxLQUFLLENBQUMsTUFBTSxFQUFFLEtBQUssQ0FBQyxJQUFJLEVBQUUsT0FBTyxFQUFFLFNBQVMsRUFBRSxTQUFTLENBQUMsQ0FBQztJQUV0RSxPQUFPLENBQUMsT0FBTyxDQUFDLENBQUMsR0FBRyxFQUFFLEtBQUssRUFBRSxFQUFFO1FBQzdCLFlBQVksQ0FBQyxHQUFHLENBQUMsTUFBTSxFQUFFLEdBQUcsQ0FBQyxJQUFJLEVBQUUsU0FBUyxFQUFFLEtBQUssRUFBRSxTQUFTLENBQUMsQ0FBQztJQUNsRSxDQUFDLENBQUMsQ0FBQztJQUVILE1BQU0sY0FBYyxHQUFHLGdCQUFnQixDQUFDLEtBQUssQ0FBQyxNQUFNLENBQUMsQ0FBQztJQUN0RCxNQUFNLGNBQWMsR0FBRyxPQUFPLENBQUMsR0FBRyxDQUFDLENBQUMsR0FBRyxFQUFFLEVBQUUsQ0FBQyxDQUFDO1FBQzNDLE1BQU0sRUFBRSxnQkFBZ0IsQ0FBQyxHQUFHLENBQUMsTUFBTSxDQUFDO1FBQ3BDLElBQUksRUFBRSxHQUFHLENBQUMsSUFBSTtLQUNmLENBQUMsQ0FBQyxDQUFDO0lBRUosSUFBSSxDQUFDLHdCQUF3QixFQUFFLENBQUM7UUFDOUIscUZBQXFGO1FBQ3JGLHdCQUF3QixHQUFHLENBQUMsTUFBTSxNQUFNLENBQUMsMkJBQTJCLENBQUMsQ0FBQyxDQUFDLHdCQUF3QixDQUFDO0lBQ2xHLENBQUM7SUFFRCx3QkFBd0IsQ0FDdEI7UUFDRSxLQUFLLEVBQUU7WUFDTCxNQUFNLEVBQUUsY0FBYztZQUN0QixJQUFJLEVBQUUsS0FBSyxDQUFDLElBQUk7U0FDakI7UUFDRCxPQUFPLEVBQUUsY0FBYztLQUN4QixFQUNELFFBQVEsQ0FDVCxDQUFDO0FBQ0osQ0FBQyxDQUFDO0FBRUYsSUFBSSxhQUFnRyxDQUFDO0FBRXJHLE1BQU0sVUFBVSxZQUFZLENBQUMsTUFBb0IsRUFBRSxJQUFTLEVBQUUsSUFBWSxFQUFFLEtBQWMsRUFBRSxTQUFTLEdBQUcsSUFBSTtJQUMxRyxNQUFNLGdCQUFnQixHQUFHLFNBQVMsQ0FBQyxDQUFDLENBQUMsTUFBTSxDQUFDLFNBQVMsQ0FBQyxJQUFJLENBQUMsQ0FBQyxDQUFDLENBQUMsTUFBTSxDQUFDLEtBQUssQ0FBQyxJQUFJLENBQUMsQ0FBQztJQUNqRixNQUFNLFNBQVMsR0FBRyxnQkFBZ0IsQ0FBQyxNQUFNLENBQUMsQ0FBQztJQUMzQyxJQUFJLENBQUMsZ0JBQWdCLENBQUMsT0FBTyxFQUFFLENBQUM7UUFDOUIsTUFBTSxXQUFXLEdBQUcsS0FBSyxLQUFLLFNBQVMsQ0FBQyxDQUFDLENBQUMseUJBQXlCLEtBQUssRUFBRSxDQUFDLENBQUMsQ0FBQyxFQUFFLENBQUM7UUFDaEYsT0FBTyxDQUFDLElBQUksQ0FBQyxHQUFHLElBQUksSUFBSSxTQUFTLHFCQUFxQixXQUFXLEdBQUcsRUFBRSxnQkFBZ0IsQ0FBQyxLQUFLLENBQUMsQ0FBQztRQUM5RixPQUFPLEtBQUssQ0FBQztJQUNmLENBQUM7SUFDRCxPQUFPLElBQUksQ0FBQztBQUNkLENBQUM7QUFFRCxNQUFNLENBQUMsTUFBTSxxQkFBcUIsR0FBRyxLQUFLLEVBQStDLEVBQ3ZGLFFBQVEsRUFDUixPQUFPLEVBQ1AsU0FBUyxHQUFHLElBQUksR0FLakIsRUFBaUIsRUFBRTtJQUNsQixPQUFPLEVBQUUsT0FBTyxDQUFDLENBQUMsR0FBRyxFQUFFLEtBQUssRUFBRSxFQUFFO1FBQzlCLFlBQVksQ0FBQyxHQUFHLENBQUMsTUFBTSxFQUFFLEdBQUcsQ0FBQyxJQUFJLEVBQUUsU0FBUyxFQUFFLEtBQUssRUFBRSxTQUFTLENBQUMsQ0FBQztJQUNsRSxDQUFDLENBQUMsQ0FBQztJQUVILElBQUksQ0FBQyxhQUFhLEVBQUUsQ0FBQztRQUNuQiwwRUFBMEU7UUFDMUUsYUFBYSxHQUFHLENBQUMsTUFBTSxNQUFNLENBQUMsMkJBQTJCLENBQUMsQ0FBQyxDQUFDLGFBQWEsQ0FBQztJQUM1RSxDQUFDO0lBRUQsYUFBYSxDQUNYO1FBQ0UsT0FBTyxFQUFFLE9BQU8sRUFBRSxHQUFHLENBQUMsQ0FBQyxHQUFHLEVBQUUsRUFBRSxDQUFDLENBQUM7WUFDOUIsTUFBTSxFQUFFLGdCQUFnQixDQUFDLEdBQUcsQ0FBQyxNQUFNLENBQUM7WUFDcEMsSUFBSSxFQUFFLEdBQUcsQ0FBQyxJQUFJO1NBQ2YsQ0FBQyxDQUFDO0tBQ0osRUFDRCxRQUFRLENBQ1QsQ0FBQztBQUNKLENBQUMsQ0FBQztBQUVGLElBQUkseUJBQTZHLENBQUM7QUFFbEgsTUFBTSxDQUFDLE1BQU0saUNBQWlDLEdBQUcsS0FBSyxFQUNwRCxNQUFrRCxFQUNuQyxFQUFFO0lBQ2pCLE1BQU0sRUFBRSxPQUFPLEVBQUUsT0FBTyxDQUFDLENBQUMsR0FBRyxFQUFFLEtBQUssRUFBRSxFQUFFO1FBQ3RDLFlBQVksQ0FBQyxHQUFHLENBQUMsTUFBTSxFQUFFLEdBQUcsQ0FBQyxJQUFJLEVBQUUsU0FBUyxFQUFFLEtBQUssQ0FBQyxDQUFDO0lBQ3ZELENBQUMsQ0FBQyxDQUFDO0lBRUgsSUFBSSxDQUFDLHlCQUF5QixFQUFFLENBQUM7UUFDL0IsMEVBQTBFO1FBQzFFLHlCQUF5QixHQUFHLENBQUMsTUFBTSxNQUFNLENBQUMsZ0RBQWdELENBQUMsQ0FBQzthQUN6Rix5QkFBeUIsQ0FBQztJQUMvQixDQUFDO0lBQ0QseUJBQXlCLENBQUM7UUFDeEIsT0FBTyxFQUFFLE1BQU0sRUFBRSxPQUFPLEVBQUUsR0FBRyxDQUFDLENBQUMsR0FBRyxFQUFFLEVBQUUsQ0FBQyxDQUFDO1lBQ3RDLE1BQU0sRUFBRSxnQkFBZ0IsQ0FBQyxHQUFHLENBQUMsTUFBTSxDQUFDO1lBQ3BDLElBQUksRUFBRSxHQUFHLENBQUMsSUFBSTtTQUNmLENBQUMsQ0FBQztLQUNKLENBQUMsQ0FBQztBQUNMLENBQUMsQ0FBQztBQUVGLElBQUksdUJBQTJHLENBQUM7QUFFaEgsTUFBTSxDQUFDLE1BQU0sK0JBQStCLEdBQUcsS0FBSyxFQUNsRCxNQUFnRCxFQUNqQyxFQUFFO0lBQ2pCLE1BQU0sRUFBRSxPQUFPLEVBQUUsT0FBTyxDQUFDLENBQUMsR0FBRyxFQUFFLEtBQUssRUFBRSxFQUFFO1FBQ3RDLFlBQVksQ0FBQyxHQUFHLENBQUMsTUFBTSxFQUFFLEdBQUcsQ0FBQyxJQUFJLEVBQUUsU0FBUyxFQUFFLEtBQUssQ0FBQyxDQUFDO0lBQ3ZELENBQUMsQ0FBQyxDQUFDO0lBRUgsSUFBSSxDQUFDLHVCQUF1QixFQUFFLENBQUM7UUFDN0Isd0VBQXdFO1FBQ3hFLHVCQUF1QixHQUFHLENBQUMsTUFBTSxNQUFNLENBQUMsOENBQThDLENBQUMsQ0FBQyxDQUFDLHVCQUF1QixDQUFDO0lBQ25ILENBQUM7SUFDRCx1QkFBdUIsQ0FBQztRQUN0QixPQUFPLEVBQUUsTUFBTSxFQUFFLE9BQU8sRUFBRSxHQUFHLENBQUMsQ0FBQyxHQUFHLEVBQUUsRUFBRSxDQUFDLENBQUM7WUFDdEMsTUFBTSxFQUFFLGdCQUFnQixDQUFDLEdBQUcsQ0FBQyxNQUFNLENBQUM7WUFDcEMsSUFBSSxFQUFFLEdBQUcsQ0FBQyxJQUFJO1NBQ2YsQ0FBQyxDQUFDO1FBQ0gsWUFBWSxFQUFFLE1BQU0sRUFBRSxZQUFZO0tBQ25DLENBQUMsQ0FBQztBQUNMLENBQUMsQ0FBQztBQUVGOztHQUVHO0FBQ0gsU0FBUywyQkFBMkIsQ0FBQyxlQUFnRCxFQUFFLEtBQWE7SUFDbEcsT0FBTyxDQUFDLEdBQUcsSUFBZSxFQUFFLEVBQUU7UUFDNUIsTUFBTSxhQUFhLEdBQUcsb0JBQW9CLENBQUMsSUFBSSxDQUFDLENBQUM7UUFDakQsSUFBSSxDQUFDLGFBQWEsRUFBRSxDQUFDO1lBQ25CLE9BQU8sSUFBSSxDQUFDO1FBQ2QsQ0FBQztRQUVELE1BQU0sYUFBYSxHQUFHLGVBQWUsQ0FBQyxhQUFhLENBQXNDLENBQUM7UUFDMUYsSUFBSSxhQUFhLEtBQUssSUFBSSxJQUFJLENBQUMsYUFBYSxFQUFFLENBQUM7WUFDN0MsT0FBTyxJQUFJLENBQUM7UUFDZCxDQUFDO1FBRUQsNkNBQTZDO1FBQzdDLE1BQU0sT0FBTyxHQUFHLFlBQVksQ0FBQyxhQUFhLENBQUMsTUFBTSxFQUFFLGFBQWEsQ0FBQyxJQUFJLEVBQUUsaUJBQWlCLEVBQUUsS0FBSyxDQUFDLENBQUM7UUFDakcsSUFBSSxDQUFDLE9BQU8sRUFBRSxDQUFDO1lBQ2IsT0FBTyxJQUFJLENBQUM7UUFDZCxDQUFDO1FBRUQsT0FBTztZQUNMLE1BQU0sRUFBRSxnQkFBZ0IsQ0FBQyxhQUFhLENBQUMsTUFBTSxDQUFDO1lBQzlDLElBQUksRUFBRSxhQUFhLENBQUMsSUFBSTtTQUN6QixDQUFDO0lBQ0osQ0FBQyxDQUFDO0FBQ0osQ0FBQztBQUVEOztHQUVHO0FBQ0gsU0FBUyxvQkFBb0IsQ0FBQyxJQUFlO0lBQzNDLElBQUksSUFBSSxDQUFDLE1BQU0sS0FBSyxDQUFDLEVBQUUsQ0FBQztRQUN0Qix3REFBd0Q7UUFDeEQsT0FBTztZQUNMLE9BQU8sRUFBRSxJQUFJLENBQUMsQ0FBQyxDQUFnQjtZQUMvQixTQUFTLEVBQUUsSUFBSSxDQUFDLENBQUMsQ0FBVztZQUM1QixLQUFLLEVBQUUsSUFBSSxDQUFDLENBQUMsQ0FBVztTQUN6QixDQUFDO0lBQ0osQ0FBQztTQUFNLElBQUksSUFBSSxDQUFDLE1BQU0sS0FBSyxDQUFDLEVBQUUsQ0FBQztRQUM3QixtREFBbUQ7UUFDbkQsT0FBTztZQUNMLE9BQU8sRUFBRSxJQUFJLENBQUMsQ0FBQyxDQUFvQjtZQUNuQyxhQUFhLEVBQUUsSUFBSSxDQUFDLENBQUMsQ0FBa0I7U0FDeEMsQ0FBQztJQUNKLENBQUM7SUFFRCxPQUFPLENBQUMsSUFBSSxDQUFDLDhDQUE4QyxFQUFFLElBQUksQ0FBQyxDQUFDO0lBQ25FLE9BQU8sSUFBSSxDQUFDO0FBQ2QsQ0FBQztBQUVEOztHQUVHO0FBQ0gsU0FBUyxnQkFBZ0IsQ0FBQyxHQUFZO0lBQ3BDLE9BQU8sT0FBTyxHQUFHLEtBQUssVUFBVSxDQUFDO0FBQ25DLENBQUM7QUFFRCxJQUFJLGtCQUFpRyxDQUFDO0FBRXRHOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7OztHQXVCRztBQUNILE1BQU0sQ0FBQyxNQUFNLDBCQUEwQixHQUFHLEtBQUssRUFBRSxNQUEyQyxFQUFpQixFQUFFO0lBQzdHLElBQUksQ0FBQyxrQkFBa0IsRUFBRSxDQUFDO1FBQ3hCLGtFQUFrRTtRQUNsRSxrQkFBa0IsR0FBRyxDQUFDLE1BQU0sTUFBTSxDQUFDLHdDQUF3QyxDQUFDLENBQUMsQ0FBQyxrQkFBa0IsQ0FBQztJQUNuRyxDQUFDO0lBQ0QsSUFBSSxDQUFDLE1BQU0sRUFBRSxPQUFPLEVBQUUsQ0FBQztRQUNyQixxRUFBcUU7UUFDckUsTUFBTSxFQUFFLE9BQU8sRUFBRSxDQUFDLEVBQUUsR0FBRyxrQkFBa0IsRUFBRSxHQUFHLE1BQU0sSUFBSSxFQUFFLENBQUM7UUFDM0Qsa0JBQWtCLENBQUMsa0JBQWtCLENBQUMsQ0FBQztRQUN2QyxPQUFPO0lBQ1QsQ0FBQztJQUVELHNDQUFzQztJQUN0QyxNQUFNLENBQUMsT0FBTyxDQUFDLE9BQU8sQ0FBQyxDQUFDLEdBQUcsRUFBRSxLQUFLLEVBQUUsRUFBRTtRQUNwQyxJQUFJLENBQUMsZ0JBQWdCLENBQUMsR0FBRyxDQUFDLEVBQUUsQ0FBQztZQUMzQixZQUFZLENBQUUsR0FBVyxDQUFDLE1BQU0sRUFBRyxHQUFXLENBQUMsSUFBSSxFQUFFLFNBQVMsRUFBRSxLQUFLLENBQUMsQ0FBQztRQUN6RSxDQUFDO0lBQ0gsQ0FBQyxDQUFDLENBQUM7SUFFSCxNQUFNLGdCQUFnQixHQUFHLE1BQU0sQ0FBQyxPQUFPLENBQUMsR0FBRyxDQUFDLENBQUMsR0FBRyxFQUFFLEtBQUssRUFBRSxFQUFFO1FBQ3pELElBQUksZ0JBQWdCLENBQUMsR0FBRyxDQUFDLEVBQUUsQ0FBQztZQUMxQixPQUFPLDJCQUEyQixDQUFDLEdBQUcsRUFBRSxLQUFLLENBQUMsQ0FBQztRQUNqRCxDQUFDO1FBRUQsd0JBQXdCO1FBQ3hCLE9BQU87WUFDTCxNQUFNLEVBQUUsZ0JBQWdCLENBQUUsR0FBVyxDQUFDLE1BQU0sQ0FBQztZQUM3QyxJQUFJLEVBQUcsR0FBVyxDQUFDLElBQUk7U0FDeEIsQ0FBQztJQUNKLENBQUMsQ0FBQyxDQUFDO0lBRUgscURBQXFEO0lBQ3JELE1BQU0sRUFBRSxPQUFPLEVBQUUsQ0FBQyxFQUFFLEdBQUcsa0JBQWtCLEVBQUUsR0FBRyxNQUFNLENBQUM7SUFFckQsa0JBQWtCLENBQUM7UUFDakIsR0FBRyxrQkFBa0I7UUFDckIsT0FBTyxFQUFFLGdCQUFnQjtLQUMxQixDQUFDLENBQUM7QUFDTCxDQUFDLENBQUM7QUFFRjs7Ozs7Ozs7OztHQVVHO0FBQ0gsTUFBTSxDQUFDLE1BQU0sdUJBQXVCLEdBQUcsQ0FBQyxFQUN0QyxLQUFLLEVBQ0wsT0FBTyxFQUNQLFlBQVksRUFDWixXQUFXLEVBQ1gsT0FBTyxFQUNQLFFBQVEsR0FRVCxFQUFRLEVBQUU7SUFDVCxPQUFPLEVBQUUsT0FBTyxDQUFDLENBQUMsR0FBRyxFQUFFLEtBQUssRUFBRSxFQUFFO1FBQzlCLFlBQVksQ0FBQyxHQUFHLENBQUMsTUFBTSxFQUFFLEdBQUcsQ0FBQyxJQUFJLEVBQUUsU0FBUyxFQUFFLEtBQUssQ0FBQyxDQUFDO0lBQ3ZELENBQUMsQ0FBQyxDQUFDO0lBRUgsZUFBZSxDQUNiO1FBQ0UsS0FBSztRQUNMLE9BQU87UUFDUCxZQUFZO1FBQ1osV0FBVztRQUNYLE9BQU8sRUFBRSxPQUFPLEVBQUUsR0FBRyxDQUFDLENBQUMsR0FBRyxFQUFFLEVBQUUsQ0FBQyxDQUFDO1lBQzlCLE1BQU0sRUFBRSxnQkFBZ0IsQ0FBQyxHQUFHLENBQUMsTUFBTSxDQUFDO1lBQ3BDLElBQUksRUFBRSxHQUFHLENBQUMsSUFBSTtTQUNmLENBQUMsQ0FBQztLQUNKLEVBQ0QsUUFBUSxDQUNULENBQUM7QUFDSixDQUFDLENBQUMifQ==